import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';

import { Icon } from '@components/base';
import { FilterGroup as FilterGroupType } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';

import { useFilterPaperElevation } from './hooks/useFilterPaperElevation';
import FilterList from './FilterList';

type FilterGroupProps = {
  filterGroup: FilterGroupType;
  hideGroupHeading?: boolean;
  index: number;
  nestingLevel: number;
  removeFilterGroup: () => void;
  updateFilterGroup: (filterGroup: FilterGroupType) => void;
};

const FilterGroup: FC<FilterGroupProps> = ({
  filterGroup,
  hideGroupHeading,
  index,
  nestingLevel,
  removeFilterGroup,
  updateFilterGroup,
}) => {
  const { t } = useTranslation();
  const elevation = useFilterPaperElevation(nestingLevel);
  const { isReadonly } = useSearchAndFilter();

  const {
    filterDefLoad: { data: filterDefinitions },
  } = useSearchAndFilter();

  return filterDefinitions ? (
    <Paper
      elevation={elevation}
      sx={{
        bgcolor: theme =>
          theme.palette.mode === 'light'
            ? nestingLevel
              ? alpha(theme.palette.common.white, 0.5)
              : theme.palette.background.default
            : undefined,
      }}
      variant='elevation'
    >
      <Grid container p={1} spacing={1}>
        {!hideGroupHeading && (
          <Fragment>
            <Grid size='grow'>
              <Typography component='span' variant='h4'>
                {t('groupNo', { groupNo: index + 1 })}
              </Typography>
            </Grid>

            {!isReadonly && (
              <Grid size='auto'>
                <Tooltip placement='left' title={t('filter.removeGroup')}>
                  <IconButton onClick={removeFilterGroup} size='small'>
                    <Icon fontSize='inherit' name='close' />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Fragment>
        )}

        <Grid size={12}>
          <FilterList
            filterDefs={filterDefinitions}
            filterGroup={filterGroup}
            nestingLevel={nestingLevel + 1}
            onChange={updateFilterGroup}
          />
        </Grid>
      </Grid>
    </Paper>
  ) : null;
};

export default FilterGroup;
